import React from 'react';
import { Avatar, Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

export interface ContentAuthorProps {
  /** Content with author fields (display name in various places). */
  content: {
    authorName?: string;
    author_name?: string;
    authorEmail?: string;
    author_email?: string;
    createdBy?: string;
    created_by?: string;
  };
  /** Show only the avatar, only the name, or both (default). */
  show?: 'avatar' | 'name' | 'both';
  /** Avatar size in px. Default 20. */
  avatarSize?: number;
  /** Optional sx overrides on the wrapping Box. */
  sx?: SxProps<Theme>;
}

const getAuthorDisplayName = (content: ContentAuthorProps['content']): string => {
  return (
    content.authorName ??
    content.author_name ??
    content.authorEmail ??
    content.author_email ??
    content.createdBy ??
    content.created_by ??
    'Unknown'
  );
};

export const ContentAuthor: React.FC<ContentAuthorProps> = ({ content, show = 'both', avatarSize = 20, sx }) => {
  const name = getAuthorDisplayName(content);
  const initial = name.charAt(0).toUpperCase();
  return (
    <Box sx={[{ display: 'inline-flex', alignItems: 'center', gap: 1 }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}>
      {(show === 'avatar' || show === 'both') && (
        <Avatar
          sx={(theme) => ({
            width: avatarSize,
            height: avatarSize,
            fontSize: Math.max(9, Math.round(avatarSize / 2)),
            fontWeight: 700,
            flexShrink: 0,
            bgcolor: theme.palette.brand.teal,
          })}
        >
          {initial}
        </Avatar>
      )}
      {(show === 'name' || show === 'both') && (
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {name}
        </Box>
      )}
    </Box>
  );
};

export default ContentAuthor;
